import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { balloonTops, BUBBLE_FONT_FRAC, BUBBLE_PAD_FRAC, TAIL_ROOM, type BubbleBox, type PanelText, type PanelView } from '@lc/contracts';

/** Manga art ink and paper: part of the printed-page look, the same as the app's balloons. */
const INK = '#1D1A24';
const PAPER = '#FFFFFF';
const CAPTION = '#FBF3E4';
const THOUGHT = '#F5F1FF';

type Lettered = PanelText & { box: BubbleBox };
const isLettered = (t: PanelText): t is Lettered => t.box !== null;

/**
 * One panel at a given width, lettered like in the app: an AI-drawn page carries its words in the
 * art; other panels get their balloons from the server's boxes, pushed down when one grows into the next.
 */
export function MangaPanel({ panel, width }: { panel: PanelView; width: number }) {
  const aspect = panel.width > 0 && panel.height > 0 ? panel.width / panel.height : 1;
  const height = width / aspect;
  const inArt = panel.lettering === 'in_art';
  const lettered = useMemo(() => (inArt ? [] : panel.texts.filter(isLettered)), [inArt, panel.texts]);
  const [heights, setHeights] = useState<number[]>([]);
  const tops = useMemo(() => balloonTops(lettered.map((x) => x.box), width, height, heights), [lettered, width, height, heights]);
  const bottom = tops.reduce((m, y, i) => Math.max(m, y + (heights[i] ?? lettered[i]!.box.h * height)), 0);
  const strip = !inArt && panel.letterHeight > 0 ? Math.max(panel.letterHeight * height, bottom + TAIL_ROOM) : 0;
  // Screen readers get a drawn page's words from its transcript, since they are part of the picture.
  const alt = inArt ? [panel.altText, ...panel.texts.map((x) => (x.speakerName ? `${x.speakerName}：${x.text}` : x.text))].join(' ') : panel.altText;
  return (
    <div className="mp" style={{ width, height: strip + height }}>
      <img className="mp-art" src={panel.imageUrl} alt={alt} style={{ top: strip, width, height }} decoding="async" />
      {lettered.map((text, i) => (
        <Balloon
          key={i}
          text={text}
          panelWidth={width}
          panelHeight={height}
          top={tops[i]!}
          onHeight={(h) => setHeights((prev) => (Math.abs((prev[i] ?? 0) - h) < 1 ? prev : Object.assign([...prev], { [i]: h })))}
        />
      ))}
    </div>
  );
}

/** One balloon, thought or caption, sized from the panel the way the app sizes it. */
function Balloon({ text, panelWidth, panelHeight, top, onHeight }: { text: Lettered; panelWidth: number; panelHeight: number; top: number; onHeight: (h: number) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const report = useRef(onHeight);
  report.current = onHeight;
  const { box } = text;
  const width = Math.max(64, box.w * panelWidth);
  const minHeight = box.h * panelHeight;
  const caption = box.style === 'caption';
  const thought = box.style === 'thought';
  const fontSize = Math.round(Math.max(12, panelWidth * BUBBLE_FONT_FRAC));
  const named = !!text.speakerName && !caption;
  const nameSize = Math.max(10, Math.round(fontSize * 0.72));
  const padY = Math.round(panelWidth * BUBBLE_PAD_FRAC.y * (caption ? 0.7 : 1));

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => report.current(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} className="mp-balloon" style={{ left: box.x * panelWidth, top, width, minHeight }}>
      <div
        className="mp-body"
        style={{
          background: caption ? CAPTION : thought ? THOUGHT : PAPER,
          borderColor: INK,
          borderWidth: caption ? 1.5 : 2,
          borderStyle: thought ? 'dashed' : 'solid',
          borderRadius: caption ? 3 : Math.min(22, minHeight / 2 + 6),
          padding: `${padY + (named ? Math.round(nameSize * 0.5) : 0)}px ${Math.round(panelWidth * BUBBLE_PAD_FRAC.x * (caption ? 0.7 : 1))}px ${padY}px`,
          minHeight,
          color: INK,
          fontSize,
          lineHeight: `${Math.round(fontSize * 1.4)}px`,
          fontWeight: caption ? 500 : 600,
          textAlign: caption ? 'left' : 'center',
        }}
      >
        {text.text}
      </div>
      {!caption && box.tail !== 'none' ? <Tail side={box.tail} thought={thought} /> : null}
      {named ? (
        <span className="mp-name" style={{ top: -Math.round(nameSize * 0.75), maxWidth: width - 20, fontSize: nameSize, lineHeight: `${Math.round(nameSize * 1.3)}px`, background: INK, color: PAPER }}>
          {text.speakerName}
        </span>
      ) : null}
    </div>
  );
}

function Tail({ side, thought }: { side: 'left' | 'right'; thought: boolean }) {
  if (thought) {
    return (
      <span className={`mp-dots mp-${side}`}>
        <i style={{ background: THOUGHT, borderColor: INK }} />
        <i style={{ background: THOUGHT, borderColor: INK }} />
      </span>
    );
  }
  return <span className={`mp-tail mp-${side}`} style={{ background: PAPER, borderColor: INK }} />;
}
